import * as React from "react";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { describeMove, PlayedMove } from "@mancala/core";
import { useTranslation } from "react-i18next";
import { ChevronIcon } from "../Icons";
import MoveTags from "./MoveTags";
import "./MoveList.css";

// The moves of a game; the shown position is highlighted and any entry can be selected. Entry 0 is the start.
const MoveList: FunctionComponent<{
    moves: PlayedMove[],
    // Move whose position is shown.
    selectedMove: number,
    onSelect: (moveNumber: number) => void,
    playerName: (playerId: string) => string,
    // Moves of this player get an accent dot, the others a muted one.
    accentPlayerId?: string,
    // Live games list the newest move first, replays the start first.
    newestFirst?: boolean,
}> = ({ moves, selectedMove, onSelect, playerName, accentPlayerId, newestFirst }) => {
    const { t } = useTranslation();
    const [collapsed, setCollapsed] = useState<boolean>(false);
    const selectedRef = useRef<HTMLLIElement>(null);
    const listRef = useRef<HTMLOListElement>(null);

    // Keep the shown move visible by scrolling the list only, never the page.
    useEffect(() => {
        const list = listRef.current, item = selectedRef.current;
        if (!list || !item) return;
        if (item.offsetTop < list.scrollTop) list.scrollTop = item.offsetTop;
        else if (item.offsetTop + item.offsetHeight > list.scrollTop + list.clientHeight) {
            list.scrollTop = item.offsetTop + item.offsetHeight - list.clientHeight;
        }
    }, [selectedMove, moves.length, collapsed]);

    const entry = (moveNumber: number) => {
        const selected = moveNumber === selectedMove;
        const move = moveNumber > 0 ? moves[moveNumber - 1] : undefined;
        return (
            <li key={moveNumber} ref={selected ? selectedRef : undefined}>
                <button className={"mrow" + (selected ? " on" : "")} aria-current={selected} onClick={() => onSelect(moveNumber)}>
                    <span className="mn">{move ? `${moveNumber}.` : ""}</span>
                    {move && <span className="mdot" style={{ background: move.playerId === accentPlayerId ? "var(--accent)" : "var(--muted)" }} />}
                    {move && <span className="mname">{playerName(move.playerId)}</span>}
                    <span className="mpit">{move ? t("PitNumber", { pit: describeMove(moves, moveNumber).pit }) : t("Start")}</span>
                    {move && <MoveTags moves={moves} moveNumber={moveNumber} />}
                </button>
            </li>
        );
    };

    const moveNumbers = Array.from({ length: moves.length + 1 }, (_, i) => i);
    if (newestFirst) moveNumbers.reverse();

    return (
        <section className="moves" aria-label={t("Moves")}>
            <button className="mhead" aria-expanded={!collapsed} onClick={() => setCollapsed(!collapsed)}>
                <span>
                    {`${t("Moves")} `}
                    <span className="muted">{`(${moves.length})${newestFirst ? ` · ${t("NewestFirst")}` : ""}`}</span>
                </span>
                <ChevronIcon className={"chev" + (collapsed ? " closed" : "")} />
            </button>
            {!collapsed && <ol className="mlist" ref={listRef}>{moveNumbers.map(entry)}</ol>}
        </section>
    );
};

export default MoveList;
